import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  PILL_TYPE_CYCLE,
  PILL_TYPE_META,
  normalizePersonPillType,
} from '../../utils/personSearchPills';
import {
  HERO_PILL_MENU_OPTION,
  HERO_PILL_MENU_OPTION_ACTIVE,
  HERO_PILL_MENU_OPTION_IDLE,
  HERO_PILL_MENU_SHELL,
  placeHeroPillMenu,
} from '../../utils/heroPillMenuPlacement';

const PILL_STYLE = {
  name: 'border-cyan-400/45 bg-cyan-500/15 text-cyan-50 hover:border-cyan-300/60 hover:bg-cyan-500/25',
  location: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-50 hover:border-emerald-300/55 hover:bg-emerald-500/25',
  focus: 'border-sky-400/40 bg-sky-500/15 text-sky-50 hover:border-sky-300/55 hover:bg-sky-500/25',
  username: 'border-violet-400/40 bg-violet-500/15 text-violet-50 hover:border-violet-300/55 hover:bg-violet-500/25',
};

/**
 * Interactive person-entity pills inside the hero search bar.
 * Type selector: frosted horizontal strip, prefers opening above the chip.
 *
 * @param {{
 *   pills: { id: string, type: string, text: string }[],
 *   onCycleType: (pillId: string) => void,
 *   onSetType?: (pillId: string, type: string) => void,
 *   onRemove?: (pillId: string) => void,
 * }} props
 */
export default function HeroPersonPills({ pills, onCycleType, onSetType, onRemove }) {
  const [openId, setOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const anchorRefs = useRef({});

  useEffect(() => {
    if (!openId) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      const anchors = Object.values(anchorRefs.current);
      if (anchors.some((el) => el && el.contains(t))) return;
      setOpenId(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  useLayoutEffect(() => {
    if (!openId) return undefined;
    const place = () => {
      const el = anchorRefs.current[openId];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuEl = menuRef.current;
      const measuredW = menuEl?.offsetWidth || 228;
      const measuredH = menuEl?.offsetHeight || 44;
      setMenuPos(placeHeroPillMenu(rect, { width: measuredW, height: measuredH, gap: 8 }));
    };
    place();
    // Re-measure after paint (horizontal strip width varies by viewport).
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [openId]);

  if (!Array.isArray(pills) || pills.length === 0) return null;

  const openPill = pills.find((p) => p.id === openId) || null;
  const openType = openPill ? normalizePersonPillType(openPill.type) : null;

  return (
    <div
      ref={rootRef}
      className="contents"
      data-testid="hero-person-pills"
      role="list"
      aria-label="Person search tags"
    >
      {pills.map((pill) => {
        const type = normalizePersonPillType(pill.type);
        const meta = PILL_TYPE_META[type] || PILL_TYPE_META.focus;
        const style = PILL_STYLE[type] || PILL_STYLE.focus;
        const display =
          type === 'username'
            ? `@${String(pill.text || '').replace(/^@/, '')}`
            : pill.text;
        const isOpen = openId === pill.id;

        return (
          <span key={pill.id} role="listitem" className="relative inline-flex max-w-full shrink-0 items-center">
            <span
              className={`inline-flex max-w-[7.5rem] items-center gap-0.5 rounded-full border py-0.5 pl-1.5 pr-0 font-mono text-[10px] font-semibold leading-tight transition-all duration-150 sm:max-w-[14rem] sm:gap-0.5 sm:py-1 sm:pl-2.5 sm:pr-0.5 sm:text-[11px] md:max-w-none ${style}`}
            >
              <button
                ref={(node) => {
                  if (node) anchorRefs.current[pill.id] = node;
                  else delete anchorRefs.current[pill.id];
                }}
                type="button"
                title={`${meta.label}: ${display}`}
                aria-label={`${meta.label}: ${display}. Open type selector`}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                onClick={() => setOpenId((prev) => (prev === pill.id ? null : pill.id))}
                className="inline-flex min-w-0 max-w-full items-center gap-0.5 py-0.5 pr-0.5 sm:gap-1 sm:pr-1"
              >
                <span aria-hidden className="shrink-0 text-[10px] leading-none sm:text-[11px]">
                  {meta.emoji}
                </span>
                <span className="min-w-0 truncate text-left whitespace-nowrap">{display}</span>
              </button>
              {onRemove ? (
                <button
                  type="button"
                  title="Remove tag"
                  aria-label={`Remove ${display}`}
                  onClick={() => {
                    setOpenId(null);
                    onRemove(pill.id);
                  }}
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] text-slate-300/90 transition hover:bg-black/25 hover:text-white sm:h-5 sm:w-5 sm:text-[11px]"
                >
                  ×
                </button>
              ) : null}
            </span>
          </span>
        );
      })}

      {openId && openType ? (
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Tag type"
          data-placement={menuPos.placement}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 90,
          }}
          className={HERO_PILL_MENU_SHELL}
        >
          {PILL_TYPE_CYCLE.map((opt) => {
            const optMeta = PILL_TYPE_META[opt];
            const selected = opt === openType;
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  if (opt !== openType) {
                    if (typeof onSetType === 'function') {
                      onSetType(openId, opt);
                    } else {
                      let guard = 0;
                      let cur = openType;
                      while (cur !== opt && guard < PILL_TYPE_CYCLE.length) {
                        onCycleType?.(openId);
                        cur = PILL_TYPE_CYCLE[
                          (PILL_TYPE_CYCLE.indexOf(cur) + 1) % PILL_TYPE_CYCLE.length
                        ];
                        guard += 1;
                      }
                    }
                  }
                  setOpenId(null);
                }}
                className={`${HERO_PILL_MENU_OPTION} ${
                  selected ? HERO_PILL_MENU_OPTION_ACTIVE : HERO_PILL_MENU_OPTION_IDLE
                }`}
              >
                <span aria-hidden className="text-sm leading-none">
                  {optMeta.emoji}
                </span>
                <span className="hidden min-[380px]:inline">
                  {optMeta.label === 'Focus' ? 'Context' : optMeta.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
